import React, { useCallback, useEffect, useState } from 'react';

import { useDropzone } from 'react-dropzone';

import {
  Paper,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  CardActions,
  Autocomplete,
  TextField,
  createFilterOptions,
} from '@material-ui/core';

import DownloadIcon from '@material-ui/icons/Download';
import FileUploadIcon from '@material-ui/icons/FileUpload';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';

import Dialog from '../../components/dialog/base';
import { useAPI } from '../../utils/api';
import { useAuth } from '../../utils/auth';
import { getHrefUrl } from '../../utils/printOrderConvert';

const OrderCreateBatchPage = () => {
  const api = useAPI();
  const auth = useAuth();

  const [shouldPageUpdated, setShouldPageUpdated] = useState(false);
  const [doesUploadDialogOpen, setDoesUploadDialogOpen] = useState(false);
  const [importedFiles, setImportedFiles] = useState();
  const [importedFileNames, setImportedFileNames] = useState('');
  const [uploadErrorMessage, setUploadErrorMessage] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [importedResultFileName, setImportedResultFileName] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [importedResultMessage, setImportedResultMessage] = useState('');
  const [memberList, setMemberList] = useState();
  const [selectedMemberData, setSelectedMemberData] = useState({});
  const [selectedMember, setSelectedMember] = useState();
  const [memberInputValue, setMemberInputValue] = useState('');
  const [showPrint, setShowPrint] = useState(false);
  const [orderList, setOrderList] = useState([]);

  const onDrop = useCallback((acceptedFiles, fileRejections) => {
    if (fileRejections.length === 0) {
      setUploadErrorMessage('');
    } else if (fileRejections.length > 1) {
      setUploadErrorMessage('一次只能上傳一個檔案');
    } else {
      const rejectedFile = fileRejections[0];
      const rejectedReasons = rejectedFile.errors;
      const invalidTypeError = rejectedReasons.some(
        (error) => error.code === 'file-invalid-type'
      );
      if (invalidTypeError) {
        setUploadErrorMessage('只可接受格式為(.xlsm 及 .xlsx)之檔案');
      }
    }
    setImportedFiles(acceptedFiles);
  }, []);

  const printAll = () => {
    api
      .updatePrintStatus(orderList)
      .then(() => {
        const url = getHrefUrl(JSON.stringify(orderList));
        window.open(url, '_blank');
      })
      .catch(() => {});
  };

  const uploadFile = () => {
    const member_id =
      auth && auth.user && auth.user.isAdmin
        ? selectedMember.value
        : auth.user.memberID;

    api
      .uploadOrderFile(importedFiles[0], Number.parseInt(member_id))
      .then((result) => {
        if (result.length > 0) {
          if (result[0] !== 'success') {
            let message = '';

            for (const element of result) {
              message = message + element + '\n';
            }
            setImportedResultMessage(message);
            setShowPrint(false);
          } else {
            setShowPrint(true);
            const printOrderList = [];
            let ti = 0;
            for (const element of result) {
              if (ti !== 0) {
                printOrderList.push(element);
              }
              ti++;
            }
            setOrderList(printOrderList);
            setImportedResultMessage('上傳成功！');
          }
        } else {
          // console.log('uploadFile msg', message);
          setImportedResultMessage('上傳成功');
          // dialog.openAlert({ message: '上傳成功' });
        }
        setShouldPageUpdated(true);
      })
      .catch(() => {});
  };

  const downloadSample = () => {
    const member_id =
      auth && auth.user && auth.user.isAdmin
        ? selectedMember.value
        : auth.user.memberID;

    api
      .downloadOrderFile(member_id)
      .then((url) => {
        if (url !== '') {
          location.href = url;
        }
      })
      .catch(() => {});
  };

  const clearUpload = () => {
    setImportedFiles();
    setImportedFileNames('');
    setDoesUploadDialogOpen(false);
    setUploadErrorMessage('');
  };

  const getMemberDataByID = useCallback((memberID) => {
    api
      .getOneMember({
        memberID,
      })
      .then((result) => {
        setSelectedMemberData(result);
      })
      .catch(() => {});

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (auth && auth.user && auth.user.isAdmin) {
      api
        .getMembersDropDownList()
        .then((result) => {
          setMemberList(result);
        })
        .catch(() => {});
    } else {
      getMemberDataByID(auth && auth.user ? auth.user.memberID : undefined);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (memberList) setSelectedMember(memberList[0]);
  }, [memberList]);

  useEffect(() => {
    if (selectedMember) {
      getMemberDataByID(selectedMember.value);
    }
  }, [selectedMember, getMemberDataByID]);

  useEffect(() => {
    setShouldPageUpdated(false);
  }, [shouldPageUpdated]);

  useEffect(() => {
    if (!importedFiles) return;

    const fileNames = [];
    importedFiles.map((file) => {
      fileNames.push(file.name);
    });
    setImportedFileNames(fileNames.join(', '));
  }, [importedFiles]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    maxFiles: 1,
    accept: '.xlsm, .xlsx',
  });

  const filterOptions = createFilterOptions({
    limit: 20,
  });

  return (
    <>
      {memberList && selectedMember !== undefined && memberList.length > 0 ? (
        <Autocomplete
          options={memberList}
          getOptionLabel={(option) => option.text || ''}
          renderOption={(properties, option) => (
            <li {...properties}>
              <Typography noWrap>{option.text}</Typography>
            </li>
          )}
          renderInput={(parameters) => (
            <TextField
              {...parameters}
              label="選擇會員"
              name="selectedMember"
              fullWidth
            />
          )}
          filterOptions={filterOptions}
          value={selectedMember}
          onChange={(event, newValue) => {
            setSelectedMember(newValue);
          }}
          inputValue={memberInputValue}
          onInputChange={(event, newInputValue) => {
            setMemberInputValue(newInputValue);
          }}
        />
      ) : undefined}

      <Paper
        elevation={4}
        sx={{
          padding: 2,
          paddingBottom: 0,
          marginTop: 4,
          marginBottom: 8,
          maxWidth: '900px',
          marginLeft: 'auto',
          marginRight: 'auto',
          display: 'flex',
          flexWrap: 'wrap',
        }}
      >
        <Box marginRight={4} marginBottom={2}>
          <Typography display="inline" fontWeight="bold">
            會員編號：
          </Typography>
          <Typography display="inline">
            {selectedMemberData.accountCode}
          </Typography>
        </Box>
        <Box variant="body" marginRight={4} marginBottom={2}>
          <Typography display="inline" fontWeight="bold">
            中文名稱：
          </Typography>
          <Typography display="inline">{selectedMemberData.name}</Typography>
        </Box>
      </Paper>

      <Box
        sx={{
          // width: { xs: '100%' },
          maxWidth: '900px',
          marginLeft: { xs: 'auto', md: 'auto !important' },
          marginRight: 'auto',
          position: 'relative',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
          columnGap: { xs: 0, md: 2 },
          rowGap: { xs: 2, md: 0 },
        }}
      >
        <Box sx={{ position: 'relative' }}>
          <Card sx={{ minWidth: 200, height: '100%' }}>
            <CardContent>
              <Typography
                variant="h6"
                component="div"
                fontWeight="bold"
                marginBottom={2}
              >
                1. 如果您想要快速匯入訂單
              </Typography>
              <Typography variant="body2" marginBottom={2} padding={1}>
                1. 您可以下載範例檔
                <br />
                2. 填入寄件人、收件人的訂單資訊。
                <br />
                3. 檔案大小限制:2MB
                <br />
                4. 檔案限制:xlsm.xlsx
                <br />
                5. 匯入已填寫好的訂單資訊。
              </Typography>
              <CardActions>
                {/* <Link
                  sx={{ width: '100%' }}
                  href="https://docs.google.com/spreadsheets/d/1NYYr6-RAqItzlQ1bmAqS0vEE1n3aXBk5/edit#gid=1484781763"
                > */}
                <Button
                  type="button"
                  fullWidth
                  variant="contained"
                  color="primary"
                  sx={{ color: 'white' }}
                  startIcon={<DownloadIcon />}
                  onClick={downloadSample}
                >
                  下載範例檔
                </Button>
                {/* </Link> */}
              </CardActions>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ position: 'relative' }}>
          <Card sx={{ minWidth: 200, height: '100%' }}>
            <CardContent
              sx={{
                height: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                flexDirection: 'column',
              }}
            >
              <Typography
                variant="h6"
                component="div"
                fontWeight="bold"
                marginBottom={2}
              >
                2. 上傳並匯入檔案
              </Typography>

              <Typography variant="body1" textAlign="center">
                {importedResultFileName ? (
                  <>
                    匯入的檔案：
                    <br />
                    {importedResultFileName}
                  </>
                ) : (
                  '請點選下方按鈕匯入檔案'
                )}
              </Typography>

              <CardActions sx={{ alignSelf: 'center', width: '100%' }}>
                <Button
                  type="button"
                  fullWidth
                  variant="contained"
                  color="primary"
                  sx={{ color: 'white' }}
                  startIcon={<FileUploadIcon />}
                  onClick={() => setDoesUploadDialogOpen(true)}
                >
                  匯入檔案
                </Button>
              </CardActions>
            </CardContent>
          </Card>
        </Box>
        {!shouldPageUpdated ? (
          <Box sx={{ position: 'relative' }}>
            <Card sx={{ minWidth: 200, height: '100%' }}>
              <CardContent>
                <Typography
                  variant="h6"
                  component="div"
                  fontWeight="bold"
                  marginBottom={2}
                >
                  3. 匯入結果
                </Typography>
                <Typography variant="body2" marginBottom={2} padding={1}>
                  {importedResultMessage}
                </Typography>
                <CardActions>
                  {/* <Link
                  sx={{ width: '100%' }}
                  href="https://docs.google.com/spreadsheets/d/1NYYr6-RAqItzlQ1bmAqS0vEE1n3aXBk5/edit#gid=1484781763"
                > */}
                  {showPrint ? (
                    <Button
                      type="button"
                      fullWidth
                      variant="contained"
                      color="primary"
                      sx={{ color: 'white' }}
                      startIcon={<DownloadIcon />}
                      onClick={printAll}
                    >
                      列印
                    </Button>
                  ) : null}

                  {/* </Link> */}
                </CardActions>
              </CardContent>
              {/* <CardContent
                sx={{
                  height: '100%',
                  display: 'flex',
                  justifyContent: 'space-between',
                  flexDirection: 'column',
                }}
              >
                <Typography
                  variant="h6"
                  component="div"
                  fontWeight="bold"
                  marginBottom={2}
                >
                  3. 匯入結果
                </Typography>

                <Typography
                  variant="body1"
                  fontWeight="bold"
                  textAlign="center"
                >
                  {importedResultMessage}
                </Typography>
              </CardContent> */}
            </Card>
          </Box>
        ) : undefined}
      </Box>

      <Dialog
        open={doesUploadDialogOpen}
        title="上傳檔案"
        confirmText="匯入"
        confirmColor="primary"
        cancelColor="error"
        onClose={() => setDoesUploadDialogOpen(false)}
        onCancel={clearUpload}
        onConfirm={() => {
          setDoesUploadDialogOpen(false);
          uploadFile();
        }}
        isConfirm={true}
        sx={{ margin: 0 }}
      >
        {uploadErrorMessage ? (
          <Typography variant="body2" marginBottom={1} color="error">
            {uploadErrorMessage}
          </Typography>
        ) : undefined}

        <Box
          {...getRootProps()}
          sx={{
            minWidth: '240px',
            width: { xs: '100%', sm: '360px' },
            height: '100px',
            border: '1px dashed #bbbbbb',
            padding: 3,
            borderRadius: 2,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            color: '#777777',
            marginBottom: 2,
          }}
        >
          <input id="uploadedFile" name="uploadedFile" {...getInputProps()} />
          {isDragActive ? (
            <p>放開以上傳檔案</p>
          ) : (
            <>
              <p className="text-center">
                將檔案拖曳至此。
                <br />
                或者點擊此處以上傳
                <br />
                <CloudUploadIcon
                  sx={{ fontSize: { xs: 60, sm: 72 }, color: '#aaaaaa' }}
                />
              </p>
            </>
          )}
        </Box>
        <Typography variant="body1">
          {importedFileNames ? `檔名: ${importedFileNames}` : ''}
        </Typography>
      </Dialog>
    </>
  );
};

export default OrderCreateBatchPage;
